import { useCallback, useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../i18n'
import { normalizeComplianceStandardCode } from '../../utils/complianceStandard'
import { Button } from '../ui/Button'
import { API_ENDPOINTS } from '../../config/api'
import {
  updateOrganizationStandardGrants,
  previewGrantBackfill,
  backfillOrganizationGrants,
  previewActivationBackfill,
  backfillOrganizationActivations,
  type StandardGrantRow,
} from '../../services/standardAccessApi'
function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}` } : {}
}

type CatalogItem = { code: string; name?: string }

function formatGrantSource(
  source: string,
  t: (key: string, fallback: string) => string,
): string {
  const key = source.trim().toUpperCase()
  if (key === 'ADMIN' || key.startsWith('ADMIN_')) {
    return t('standardGovernance.sourceAdmin', 'Assigned by administrator')
  }
  if (key === 'BACKFILL') {
    return t('standardGovernance.sourceBackfill', 'Imported from existing activity')
  }
  if (key === 'STARTER_GUIDE') {
    return t('standardGovernance.sourceStarterGuide', 'Starter Guide')
  }
  return source
}

type Props = {
  organizationId: string
}

export default function OrganizationStandardGrantsPanel({ organizationId }: Props) {
  const { t } = useI18n()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [catalog, setCatalog] = useState<CatalogItem[]>([])
  const [grants, setGrants] = useState<StandardGrantRow[]>([])
  const [enabled, setEnabled] = useState<Record<string, boolean>>({})
  const [backfillPreview, setBackfillPreview] = useState<{
    wouldCreate: string[]
    alreadyGranted: string[]
  } | null>(null)
  const [backfillBusy, setBackfillBusy] = useState(false)
  const [activationPreviewCount, setActivationPreviewCount] = useState<number | null>(null)

  const load = useCallback(async () => {
    if (!organizationId) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_ORG_STANDARD_GRANTS(organizationId), {
        headers: getAuthHeaders(),
      })
      const body = await res.json()
      if (!res.ok) throw new Error(body?.message || 'Failed to load')
      const data = body.data || body
      const grantRows: StandardGrantRow[] = data.grants || []
      const cat: CatalogItem[] = (data.catalog || []).map((c: { code: string; name?: string }) => ({
        code: c.code,
        name: c.name,
      }))
      setCatalog(cat)
      setGrants(grantRows)
      const map: Record<string, boolean> = {}
      for (const item of cat) {
        const canon = normalizeComplianceStandardCode(item.code)
        const g = grantRows.find(
          (r) => normalizeComplianceStandardCode(r.standardCode) === canon,
        )
        map[canon] = g ? g.enabled !== false : false
      }
      for (const g of grantRows) {
        const canon = normalizeComplianceStandardCode(g.standardCode)
        if (!(canon in map)) map[canon] = g.enabled !== false
      }
      setEnabled(map)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Load failed')
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void load()
  }, [load])

  const rows = useMemo(() => {
    const byCanonical = new Map<string, string>()
    for (const code of [...catalog.map((c) => c.code), ...grants.map((g) => g.standardCode)]) {
      const canon = normalizeComplianceStandardCode(code)
      if (!canon || canon === 'ALL') continue
      if (!byCanonical.has(canon)) byCanonical.set(canon, code)
    }
    return Array.from(byCanonical.values()).sort((a, b) => a.localeCompare(b))
  }, [catalog, grants])

  const grantByCanonical = useMemo(() => {
    const m = new Map<string, StandardGrantRow>()
    grants.forEach((g) => {
      const canon = normalizeComplianceStandardCode(g.standardCode)
      if (!canon) return
      const prev = m.get(canon)
      if (!prev || (g.enabled && !prev.enabled)) m.set(canon, g)
    })
    return m
  }, [grants])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const standards = rows.map((code) => {
        const canon = normalizeComplianceStandardCode(code)
        return { standardCode: canon, enabled: !!enabled[canon] }
      })
      await updateOrganizationStandardGrants(organizationId, standards)
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const grantedActiveCount = grants.filter((g) => g.enabled !== false).length

  const handlePreviewBackfill = async () => {
    setBackfillBusy(true)
    setError(null)
    try {
      const preview = await previewGrantBackfill(organizationId)
      setBackfillPreview({
        wouldCreate: preview.wouldCreate,
        alreadyGranted: preview.alreadyGranted,
      })
      const actPreview = await previewActivationBackfill(organizationId)
      setActivationPreviewCount(actPreview.wouldCreate?.length ?? 0)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Preview failed')
    } finally {
      setBackfillBusy(false)
    }
  }

  const handleRunBackfill = async () => {
    setBackfillBusy(true)
    setError(null)
    try {
      await backfillOrganizationGrants(organizationId)
      await backfillOrganizationActivations(organizationId)
      await load()
      setBackfillPreview(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Backfill failed')
    } finally {
      setBackfillBusy(false)
    }
  }

  if (!organizationId) return null

  return (
    <section className="mt-4 border-t pt-4">
      <h4 className="text-sm font-semibold mb-1">
        {t('standardGovernance.organizationGrantsTitle', 'Standard access for organization')}
      </h4>
      <p className="text-xs text-gray-600 mb-3">
        {t(
          'standardGovernance.organizationGrantsHint',
          'Users in collaborative mode receive standards granted at organization level automatically.',
        )}
      </p>
      <p className="text-xs text-gray-600 mb-2">
        {t('standardGovernance.grantsVsActive', 'Granted')}: {grantedActiveCount} ·{' '}
        {t('standardGovernance.activeStandard', 'Active standards')}: catalog + grants
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        <Button
          type="button"
          size="small"
          color="outline"
          disabled={backfillBusy || loading}
          onClick={() => void handlePreviewBackfill()}
        >
          {t('standardGovernance.previewBackfill', 'Preview backfill')}
        </Button>
        <Button
          type="button"
          size="small"
          color="secondary"
          disabled={backfillBusy || loading}
          onClick={() => void handleRunBackfill()}
        >
          {t('standardGovernance.backfillGrants', 'Backfill grants from existing activity')}
        </Button>
      </div>
      {backfillPreview ? (
        <div className="text-xs mb-3 p-2 bg-gray-50 rounded border">
          <p>
            {t('standardGovernance.existingActivity', 'Existing activity')}:{' '}
            {backfillPreview.wouldCreate.length} {t('standardGovernance.wouldCreate', 'to create')},{' '}
            {backfillPreview.alreadyGranted.length} {t('standardGovernance.alreadyGranted', 'already granted')}
          </p>
          {activationPreviewCount != null ? (
            <p>
              {t('standardGovernance.activationSource', 'Activation source')}: ~{activationPreviewCount}{' '}
              {t('standardGovernance.activationsWouldCreate', 'activations would be created')}
            </p>
          ) : null}
          {backfillPreview.wouldCreate.length > 0 ? (
            <p className="mt-1 text-gray-700">{backfillPreview.wouldCreate.join(', ')}</p>
          ) : null}
        </div>
      ) : null}
      {error ? <p className="text-xs text-red-600 mb-2">{error}</p> : null}
      {loading ? (
        <p className="text-xs text-gray-500">{t('common.loading', 'Loading…')}</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-500">{t('standardGovernance.noCatalog', 'No standards in catalog.')}</p>
      ) : (
        <ul className="max-h-48 overflow-y-auto text-sm space-y-1 mb-3">
          {rows.map((code) => {
            const canon = normalizeComplianceStandardCode(code)
            const g = grantByCanonical.get(canon)
            const catalogName =
              catalog.find((c) => normalizeComplianceStandardCode(c.code) === canon)?.name ||
              catalog.find((c) => c.code === code)?.name
            return (
              <li key={canon} className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 min-w-[12rem]">
                  <input
                    type="checkbox"
                    checked={!!enabled[canon]}
                    onChange={(e) =>
                      setEnabled((prev) => ({ ...prev, [canon]: e.target.checked }))
                    }
                  />
                  <span>{catalogName || code}</span>
                </label>
                {g?.enabled !== false && g?.source ? (
                  <span className="text-xs text-gray-500" title={g.source}>
                    {formatGrantSource(g.source, t)}
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
      <Button type="button" size="small" color="primary" disabled={saving || loading} onClick={() => void handleSave()}>
        {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
      </Button>
    </section>
  )
}
